import {
    markFieldFocus,
    trackSelection,
} from '@/apiCalls/uniqueIDTransferHelper';
import { FieldLabel } from '@/components/FieldLabel';
import {
    ELoanUsage,
    selectLoanTerm,
    selectLoanUsage,
    setBalloon,
    setLoanTerm,
    setLoanUsage,
} from '@/context/borrower';
import { selectIsLoading, selectIsNZ, selectPartner } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useFieldCopy } from '@/copy';
import { getNumber } from '@/utils/valueFormat';

import { LoanTermDisabled } from '../LoanTermDisabled';

export interface IProps {
    baseId: string;
}

export const testId = 'LoanTerm';

export function LoanTerm({ baseId }: IProps): JSX.Element {
    // ***** Redux *****
    const fields = useFieldCopy();
    const currentState = useAppSelector(selectLoanTerm);
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectIsLoading);
    const partner = useAppSelector(selectPartner);
    const loanUsage = useAppSelector(selectLoanUsage);
    const isNZ = useAppSelector(selectIsNZ);

    const minLoanTerm = partner?.minLoanTerm || 1;
    const maxLoanTerm = isNZ ? 5 : partner?.maxLoanTerm || 7;

    // ***** Event Handlers *****
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const value = event.target.value;
        const intValue = getNumber(value, maxLoanTerm);
        if (intValue === currentState) return;
        dispatch(setLoanTerm(intValue));

        // A raw slider rather than a shared Input, so it carried no tracking at
        // all — on the screen every CarExpert session starts on. Records that the
        // control was used and how many times it was moved, never the value.
        trackSelection(`${baseId}-input-loanTerm`);
        if (intValue > 5) {
            if (loanUsage === ELoanUsage.NOVATED) {
                dispatch(setLoanUsage(ELoanUsage.PERSONAL));
            }
        }
    }

    if (currentState > maxLoanTerm) {
        dispatch(setBalloon(0));
    }

    // Anchored to the partner's own minimum rather than a hardcoded 1, so
    // the fill matches the scale printed under the track.
    const currentPercent =
        ((currentState - minLoanTerm) / (maxLoanTerm - minLoanTerm)) * 100;

    if (minLoanTerm === maxLoanTerm) {
        return <LoanTermDisabled baseId={baseId} />;
    }

    // ***** Render *****
    return (
        <div className='py-[7px]' data-testid={testId}>
            {/* The value sits beside the label rather than above the track,
                so the row reads as one statement and the track keeps the full
                width for its own scale. */}
            <div className='_flex items-center justify-between gap-2'>
                <div className='min-w-0'>
                    <FieldLabel
                        title={fields.loanTerm}
                        required
                        tooltipContent='The longer the term the lower the repayments.'
                    />
                </div>
                <label
                    className='font-bold text-sm shrink-0 whitespace-nowrap pb-1'
                    htmlFor={`${baseId}-input-loanTerm`}
                >
                    {currentState} {currentState === 1 ? 'year' : 'years'}
                </label>
            </div>

            <div className='flex-center flex-col'>
                <div className='relative w-full h-1 rounded-full'>
                    <div
                        className='absolute top-0 h-full bg-primary rounded-full'
                        style={{ width: `${currentPercent}%` }}
                    ></div>
                    <div
                        className='absolute top-0 h-full bg-gray-300 rounded-full'
                        style={{
                            width: `${100 - currentPercent}%`,
                            left: `${currentPercent}%`,
                        }}
                    ></div>
                    <input
                        id={`${baseId}-input-loanTerm`}
                        type='range'
                        min={minLoanTerm}
                        max={maxLoanTerm}
                        value={currentState}
                        className='absolute w-full h-full cursor-pointer range-sm top-1'
                        step='1'
                        onChange={handleChange}
                        onFocus={(): void =>
                            markFieldFocus(`${baseId}-input-loanTerm`)
                        }
                        disabled={isLoading ? true : false}
                        style={{
                            WebkitAppearance: 'none',
                            background: 'transparent',
                            outline: 'none',
                            height: '20px', // increase the height to match the height of the thumb
                            left: 0,
                        }}
                    />
                    <style>
                        {`
                        #${baseId}-input-loanTerm::-webkit-slider-thumb {
                            -webkit-appearance: none;
                            width: 20px;
                            height: 20px;
                            border-radius: 10px;
                            /* A ring rather than a solid dot: the handle reads
                               as something to grab, and the track colour stays
                               legible underneath it. */
                            background: #ffffff;
                            border: 3px solid var(--primaryColor);
                            box-sizing: border-box;
                            cursor: pointer;
                            margin-top: -8px;
                            position: relative;
                            z-index: 0;
                        }

                        #${baseId}-input-loanTerm::-moz-range-thumb {
                            width: 20px;
                            height: 20px;
                            border-radius: 10px;
                            background: #ffffff;
                            border: 3px solid var(--primaryColor);
                            box-sizing: border-box;
                            cursor: pointer;
                            z-index: 2;
                        }
                    `}
                    </style>
                </div>

                {/* The ends of the scale, so the range is readable without
                    dragging the handle to find it. */}
                <div className='_flex justify-between w-full pt-2 text-xs text-textBody'>
                    <span>
                        {minLoanTerm} {fields.loanTermMinUnit}
                    </span>
                    <span>
                        {maxLoanTerm} {fields.loanTermMaxUnit}
                    </span>
                </div>
            </div>
        </div>
    );
}
